import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Loader2, Pencil, Plus, RefreshCw, Search, UserRound, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  CHART_SLICE_COLORS,
  aggregateChart,
  chartTypeLabel,
  chartVisibilityLabel,
  mapChartDefinitionRow,
  readCustomFieldValue,
  sanitizeInlineLabelHtml,
  sortChartDefinitions,
  stripHtmlToText,
  type ChartAggregate,
  type ChartDefinitionRow,
  type ChartType,
} from '../../lib/chartDefinitions';
import { parseOptions } from '../../lib/subscriberCustomFields';
import { subscriberRoleLabel } from '../../lib/subscriberRoles';
import ChartDefinitionModal from './ChartDefinitionModal';
import ChartPersonPickerModal, { type ChartPersonOption } from './ChartPersonPickerModal';

type FieldMeta = { key: string; label: string; options: string[] };
type ChannelMeta = { slug: string; label: string };
type SubRow = {
  id: string;
  name: string | null;
  email: string;
  role: string | null;
  channels: unknown;
  custom_data: unknown;
};

const FOCUS_RING = 'ring-2 ring-sky-400 ring-offset-1 dark:ring-offset-gray-800';
const FOCUS_BAR = '#0ea5e9';

function RichLabel({
  html,
  className,
  suffix,
}: {
  html: string;
  className?: string;
  suffix?: string;
}) {
  const safe = sanitizeInlineLabelHtml(html);
  return (
    <span className={className}>
      <span dangerouslySetInnerHTML={{ __html: safe }} />
      {suffix ? <span>{suffix}</span> : null}
    </span>
  );
}

function personPhotoUrl(customData: unknown): string | null {
  const url = readCustomFieldValue(customData, 'photo_url');
  return url.startsWith('http') ? url : null;
}

function PersonAvatar({
  name,
  email,
  customData,
  size = 'md',
}: {
  name: string | null;
  email: string;
  customData: unknown;
  size?: 'sm' | 'md';
}) {
  const photo = personPhotoUrl(customData);
  const initials = (name || email)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';
  const box = size === 'sm' ? 'w-9 h-9 text-xs' : 'w-12 h-12 text-sm';

  if (photo) {
    return (
      <img
        src={photo}
        alt=""
        className={`${box} rounded-full object-cover shrink-0 border border-sky-200 dark:border-sky-700`}
      />
    );
  }

  return (
    <span
      className={`${box} rounded-full shrink-0 flex items-center justify-center font-semibold bg-sky-100 dark:bg-sky-900/50 text-sky-800 dark:text-sky-200 border border-sky-200 dark:border-sky-700`}
      aria-hidden
    >
      {initials}
    </span>
  );
}

function HorizontalBars({ agg, focusLabel }: { agg: ChartAggregate; focusLabel: string | null }) {
  return (
    <div className="space-y-2.5">
      {agg.buckets.map((b) => {
        const isFocus = Boolean(focusLabel && b.label === focusLabel);
        return (
          <div key={b.label} className={isFocus ? 'rounded-md -mx-1 px-1 py-0.5 bg-sky-50/80 dark:bg-sky-900/20' : ''}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <RichLabel
                html={b.label}
                suffix={isFocus ? ' · you' : undefined}
                className={`text-sm truncate min-w-0 ${
                  isFocus
                    ? 'font-semibold text-sky-800 dark:text-sky-200'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              />
              <span className="text-xs tabular-nums text-gray-500 shrink-0">
                {b.count} ({b.percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full rounded-full ${isFocus ? '' : 'bg-emerald-500'}`}
                style={{
                  width: `${Math.min(100, Math.max(0, b.percentage))}%`,
                  ...(isFocus ? { backgroundColor: FOCUS_BAR } : {}),
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VerticalBars({ agg, focusLabel }: { agg: ChartAggregate; focusLabel: string | null }) {
  const max = Math.max(...agg.buckets.map((b) => b.count), 1);
  return (
    <div className="flex items-end gap-2 h-40 pt-2">
      {agg.buckets.map((b, i) => {
        const isFocus = Boolean(focusLabel && b.label === focusLabel);
        const plain = stripHtmlToText(b.label);
        return (
          <div
            key={b.label}
            className="flex-1 min-w-0 flex flex-col items-center gap-1 h-full justify-end"
          >
            <span className={`text-[10px] tabular-nums ${isFocus ? 'font-semibold text-sky-600' : 'text-gray-500'}`}>
              {b.count}
            </span>
            <div className="w-full flex-1 flex items-end">
              <div
                className={`w-full rounded-t min-h-[2px] ${isFocus ? FOCUS_RING : ''}`}
                style={{
                  height: `${(b.count / max) * 100}%`,
                  backgroundColor: isFocus
                    ? FOCUS_BAR
                    : CHART_SLICE_COLORS[i % CHART_SLICE_COLORS.length],
                }}
                title={`${plain}: ${b.count} (${b.percentage.toFixed(1)}%)${isFocus ? ' — selected person' : ''}`}
              />
            </div>
            <RichLabel
              html={b.label}
              className={`text-[10px] truncate w-full text-center leading-tight ${
                isFocus ? 'font-semibold text-sky-700 dark:text-sky-300' : 'text-gray-500 dark:text-gray-400'
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}

function PieChart({ agg, focusLabel }: { agg: ChartAggregate; focusLabel: string | null }) {
  const slices = agg.buckets.filter((b) => b.count > 0);
  if (slices.length === 0) {
    return <p className="text-sm text-gray-500 py-4 text-center">No slice data.</p>;
  }

  let cumulative = 0;
  const gradientParts = slices.map((b, i) => {
    const start = cumulative;
    cumulative += b.percentage;
    const isFocus = Boolean(focusLabel && b.label === focusLabel);
    const color = isFocus ? FOCUS_BAR : CHART_SLICE_COLORS[i % CHART_SLICE_COLORS.length];
    return `${color} ${start}% ${cumulative}%`;
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div
        className="w-36 h-36 rounded-full shrink-0 border border-gray-200 dark:border-gray-600"
        style={{ background: `conic-gradient(${gradientParts.join(', ')})` }}
        role="img"
        aria-label="Pie chart"
      />
      <ul className="flex-1 w-full space-y-1.5 min-w-0">
        {slices.map((b, i) => {
          const isFocus = Boolean(focusLabel && b.label === focusLabel);
          return (
            <li
              key={b.label}
              className={`flex items-center gap-2 text-sm rounded-md px-1 py-0.5 ${
                isFocus ? 'bg-sky-50 dark:bg-sky-900/25' : ''
              }`}
            >
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{
                  backgroundColor: isFocus
                    ? FOCUS_BAR
                    : CHART_SLICE_COLORS[i % CHART_SLICE_COLORS.length],
                }}
              />
              <RichLabel
                html={b.label}
                suffix={isFocus ? ' · you' : undefined}
                className={`truncate flex-1 min-w-0 ${
                  isFocus
                    ? 'font-semibold text-sky-800 dark:text-sky-200'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              />
              <span className="tabular-nums text-xs text-gray-500 shrink-0">
                {b.count} ({b.percentage.toFixed(1)}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ChartBody({
  agg,
  format,
  focusLabel,
}: {
  agg: ChartAggregate;
  format: ChartType;
  focusLabel: string | null;
}) {
  if (format === 'pie') return <PieChart agg={agg} focusLabel={focusLabel} />;
  if (format === 'bar') return <VerticalBars agg={agg} focusLabel={focusLabel} />;
  return <HorizontalBars agg={agg} focusLabel={focusLabel} />;
}

function ChartCard({
  agg,
  focusPerson,
  onEdit,
}: {
  agg: ChartAggregate;
  focusPerson: ChartPersonOption | null;
  onEdit: (chart: ChartDefinitionRow) => void;
}) {
  const formats = agg.chart.chart_types?.length ? agg.chart.chart_types : [agg.chart.chart_type];
  const [format, setFormat] = useState<ChartType>(formats[0] ?? 'horizontal_bar');

  useEffect(() => {
    if (!formats.includes(format)) {
      setFormat(formats[0] ?? 'horizontal_bar');
    }
  }, [formats, format]);

  const focusLabel = focusPerson
    ? readCustomFieldValue(focusPerson.custom_data, agg.chart.field_key) || null
    : null;
  const focusBucket = focusLabel ? agg.buckets.find((b) => b.label === focusLabel) : null;
  const focusMissing = Boolean(focusPerson && !focusLabel);
  const roleLabel = focusPerson ? subscriberRoleLabel(focusPerson.role) : '';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden flex flex-col">
      <div className="p-5 flex-1">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{agg.chart.title}</h2>
            {agg.chart.teaser ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{agg.chart.teaser}</p>
            ) : null}
            <p className="text-xs text-gray-400 mt-2 font-mono">
              {agg.chart.field_key} · {chartVisibilityLabel(agg.chart.visibility)}
              {agg.chart.audience_channel ? ` · #${agg.chart.audience_channel}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onEdit(agg.chart)}
            className="shrink-0 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            title="Edit"
            aria-label="Edit chart"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-200">
            <Users className="h-3.5 w-3.5" />
            Audience {agg.audienceTotal}
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-xs text-emerald-800 dark:text-emerald-200">
            Answered {agg.answered}
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/30 text-xs text-amber-800 dark:text-amber-200">
            Unanswered {agg.unanswered}
          </span>
          {formats.length > 1 ? (
            <div className="ml-auto flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              {formats.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFormat(t)}
                  className={`px-2.5 py-1 text-xs transition-colors ${
                    format === t
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {chartTypeLabel(t)}
                </button>
              ))}
            </div>
          ) : (
            <span className="ml-auto text-xs text-gray-400">{chartTypeLabel(format)}</span>
          )}
        </div>

        {agg.answered === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No answers in this audience yet.</p>
        ) : (
          <ChartBody agg={agg} format={format} focusLabel={focusLabel} />
        )}
      </div>

      {focusPerson ? (
        <div className="border-t border-sky-200 dark:border-sky-800 bg-sky-50/90 dark:bg-sky-950/40 px-5 py-3.5">
          <div className="flex items-start gap-3">
            <PersonAvatar
              name={focusPerson.name}
              email={focusPerson.email}
              customData={focusPerson.custom_data}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-sky-950 dark:text-sky-100 truncate">
                {focusPerson.name || focusPerson.email}
              </p>
              {roleLabel ? (
                <p className="text-xs text-sky-800/80 dark:text-sky-300/90 mt-0.5">{roleLabel}</p>
              ) : (
                <p className="text-xs text-sky-700/60 dark:text-sky-400/70 mt-0.5 truncate">
                  {focusPerson.email}
                </p>
              )}
              {focusMissing ? (
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                  Looks like they haven’t answered this one yet.
                </p>
              ) : (
                <p className="text-sm text-sky-900 dark:text-sky-100 mt-2">
                  Looks like you responded{' '}
                  <RichLabel html={focusLabel ?? ''} className="font-semibold" />
                  {focusBucket ? (
                    <>
                      {' '}
                      — with{' '}
                      <span className="font-semibold tabular-nums">
                        {focusBucket.count} peer{focusBucket.count === 1 ? '' : 's'}
                      </span>{' '}
                      ({focusBucket.percentage.toFixed(1)}% of answers).
                    </>
                  ) : (
                    '.'
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ChartsView() {
  const [charts, setCharts] = useState<ChartDefinitionRow[]>([]);
  const [fields, setFields] = useState<FieldMeta[]>([]);
  const [channels, setChannels] = useState<ChannelMeta[]>([]);
  const [subscribers, setSubscribers] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ChartDefinitionRow | null>(null);
  const [personPickerOpen, setPersonPickerOpen] = useState(false);
  const [focusPerson, setFocusPerson] = useState<ChartPersonOption | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chartsRes, fieldsRes, channelsRes, subsRes] = await Promise.all([
        supabase.from('chart_definitions').select('*').order('sort_order', { ascending: true }),
        supabase
          .from('subscriber_field_definitions')
          .select('key, label, options, field_type, active')
          .eq('active', true)
          .order('key'),
        supabase.from('channels').select('slug, label').eq('active', true).order('sort_order'),
        supabase.from('subscribers').select('id, name, email, role, channels, custom_data'),
      ]);
      if (chartsRes.error) throw chartsRes.error;
      if (fieldsRes.error) throw fieldsRes.error;
      if (channelsRes.error) throw channelsRes.error;
      if (subsRes.error) throw subsRes.error;

      setCharts(
        sortChartDefinitions(
          (chartsRes.data ?? []).map((r) => mapChartDefinitionRow(r as Record<string, unknown>)),
        ),
      );
      setFields(
        (fieldsRes.data ?? []).map((f) => ({
          key: String(f.key),
          label: String(f.label),
          options: parseOptions(f.options),
        })),
      );
      setChannels(
        (channelsRes.data ?? []).map((c) => ({
          slug: String(c.slug),
          label: String(c.label),
        })),
      );
      setSubscribers((subsRes.data ?? []) as SubRow[]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load charts');
      setCharts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const fieldByKey = useMemo(() => {
    const m = new Map<string, FieldMeta>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  const people = useMemo<ChartPersonOption[]>(
    () =>
      subscribers.map((s) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        role: s.role,
        custom_data: s.custom_data,
        channels: s.channels,
      })),
    [subscribers],
  );

  const aggregates = useMemo(() => {
    return charts
      .filter((c) => c.active)
      .map((chart) => {
        const meta = fieldByKey.get(chart.field_key);
        return aggregateChart(chart, meta?.options ?? [], subscribers);
      });
  }, [charts, fieldByKey, subscribers]);

  const filteredAggs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return aggregates;
    return aggregates.filter(
      (a) =>
        a.chart.title.toLowerCase().includes(q) ||
        a.chart.slug.toLowerCase().includes(q) ||
        a.chart.field_key.toLowerCase().includes(q),
    );
  }, [aggregates, searchQuery]);

  const inactiveCharts = useMemo(() => charts.filter((c) => !c.active), [charts]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-emerald-500" />
            Charts
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Chart definitions over custom fields — audience, answered vs unanswered, and live distribution.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {focusPerson ? (
            <div className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800 text-sm text-sky-900 dark:text-sky-100 max-w-[220px]">
              <UserRound className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{focusPerson.name || focusPerson.email}</span>
              <button
                type="button"
                onClick={() => setFocusPerson(null)}
                className="p-1 rounded hover:bg-sky-100 dark:hover:bg-sky-800/50"
                title="Clear personalization"
                aria-label="Clear personalization"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setPersonPickerOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <UserRound className="h-4 w-4" />
            Customize
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            title="New chart"
            aria-label="New chart"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          placeholder="Search charts…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      ) : filteredAggs.length === 0 && inactiveCharts.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No chart definitions yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
            {filteredAggs.map((agg) => (
              <ChartCard
                key={agg.chart.id}
                agg={agg}
                focusPerson={focusPerson}
                onEdit={(c) => {
                  setEditing(c);
                  setModalOpen(true);
                }}
              />
            ))}
          </div>

          {inactiveCharts.length > 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Inactive charts</h3>
              <ul className="space-y-2">
                {inactiveCharts.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {c.title} <span className="font-mono text-xs">({c.slug})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(c);
                        setModalOpen(true);
                      }}
                      className="text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      Edit
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}

      <ChartDefinitionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        chart={editing}
        fieldOptions={fields}
        channelOptions={channels}
        onSuccess={() => void load()}
      />

      <ChartPersonPickerModal
        isOpen={personPickerOpen}
        onClose={() => setPersonPickerOpen(false)}
        people={people}
        selectedId={focusPerson?.id ?? null}
        onSelect={setFocusPerson}
      />
    </div>
  );
}
